import Vue from 'vue';
import {
  createAuth0Client,
  Auth0Client,
  User,
  // Import necessary options types
  RedirectLoginOptions,
  PopupLoginOptions,
  GetTokenSilentlyOptions,
  LogoutOptions,
  // Ensure Auth0ClientOptions is imported if extending options
  Auth0ClientOptions
} from '@auth0/auth0-spa-js';

// Define the plugin options interface (assuming it's defined above this snippet)
interface Auth0PluginOptions extends Auth0ClientOptions { // Extend base options if needed
    // Keep your specific options structure if different
    domain: string;
    clientId: string;
    authorizationParams: {
        audience?: string;
        redirect_uri: string;
        scope: string;
    };
    cacheLocation?: 'localstorage' | 'memory';
    useRefreshTokens?: boolean;
    useRefreshTokensFallback?: boolean;
}

// Define the shape of the Auth0 plugin instance (assuming it's defined above)
interface Auth0Plugin extends Vue {
  $auth: {
    loading: boolean;
    isAuthenticated: boolean;
    user: User | undefined;
    auth0Client: Auth0Client | null; // Keep this for potential direct access if needed, though methods are safer
    loginWithRedirect(options?: RedirectLoginOptions): Promise<void>;
    loginWithPopup(options?: PopupLoginOptions): Promise<void>;
    handleRedirectCallback(url?: string): Promise<any>;
    getTokenSilently(options?: GetTokenSilentlyOptions): Promise<string>;
    logout(options?: LogoutOptions): void;
    getUser(): Promise<User | undefined>;
  };
}


// --- Store the initialization promise globally within the plugin's scope ---
let initializationPromise: Promise<Auth0Client> | null = null;

export const Auth0Plugin = {
  install(VueInstance: typeof Vue, options: Auth0PluginOptions) {
    // --- Reactive State ---
    const authService = VueInstance.observable({
      loading: true, // Represents overall loading (init or callback handling)
      isAuthenticated: false,
      user: undefined as User | undefined,
      auth0Client: null as Auth0Client | null, // Keep track of the client instance
    });

    // --- Helper function to ensure client is ready ---
    // Returns the initialized client or throws if initialization failed
    async function ensureClientReady(): Promise<Auth0Client> {
        if (authService.auth0Client) {
            // If client already exists, return it
            return authService.auth0Client;
        }
        // If client doesn't exist, wait for the initialization promise
        if (!initializationPromise) {
            // This should ideally not happen if install is called correctly
            throw new Error("Auth0 plugin initialization promise not found.");
        }
        console.log('[Auth Plugin] Client not ready, awaiting initializationPromise...');
        const client = await initializationPromise; // Wait for initializeAuth0/createAuth0Client
        console.log('[Auth Plugin] InitializationPromise resolved.');
        if (!client || !authService.auth0Client) {
           // If client is still null after waiting, initialization failed
           throw new Error("Auth0 client failed to initialize.");
        }
        return authService.auth0Client; // Return the initialized client
    }

    // --- Methods ---
    const methods = {
      async loginWithRedirect(opts?: RedirectLoginOptions): Promise<void> {
        try {
            console.log('[Auth Plugin] loginWithRedirect called.');
            const client = await ensureClientReady(); // Wait for init if needed
            console.log('[Auth Plugin] Client ready for loginWithRedirect.');
            await client.loginWithRedirect(opts);
        } catch (error) {
            console.error('[Auth Plugin] Error during loginWithRedirect:', error);
            // Handle error appropriately, maybe set an error state
        }
      },

      // ... (Similarly wrap loginWithPopup if used) ...

      async handleRedirectCallback(url?: string): Promise<any> {
        console.log('[Auth Plugin] handleRedirectCallback method invoked.');
        // 1. Ensure client is initialized BEFORE proceeding
        const client = await ensureClientReady();
        console.log('[Auth Plugin] Client ready for handleRedirectCallback.');

        // 2. Set loading state specifically for this operation
        authService.loading = true;
        try {
          // 3. Perform the core SDK operation
          console.log('[Auth Plugin] Calling client.handleRedirectCallback...');
          const result = await client.handleRedirectCallback(url);
          console.log('[Auth Plugin] client.handleRedirectCallback finished successfully.');

          // 4. Update reactive state
          authService.user = await client.getUser() || undefined;
          authService.isAuthenticated = await client.isAuthenticated(); // Use SDK's check after callback
          console.log('[Auth Plugin] State updated after callback. isAuthenticated:', authService.isAuthenticated);
          return result;
        } catch (e) {
          console.error('[Auth Plugin] Error during client.handleRedirectCallback:', e);
          // Reset state on error
          authService.isAuthenticated = false;
          authService.user = undefined;
          throw e; // Re-throw error for Callback.vue to catch
        } finally {
          // 5. Reset loading state for this operation
          console.log('[Auth Plugin] handleRedirectCallback finally block executing.');
          authService.loading = false;
          console.log('[Auth Plugin] handleRedirectCallback loading set to false.');
        }
      },

      async getTokenSilently(opts?: GetTokenSilentlyOptions): Promise<string> {
        // Add similar ensureClientReady() wrapper
        const client = await ensureClientReady();
        return client.getTokenSilently(opts);
      },

      logout(opts?: LogoutOptions): void {
        // Ensure client is ready before attempting logout
        ensureClientReady().then(client => {
            client.logout(opts);
        }).catch(error => {
            console.error("[Auth Plugin] Failed to logout, client may not be ready:", error);
            // Optional: Fallback behavior? e.g., clear local state and redirect manually
            // authService.isAuthenticated = false;
            // authService.user = undefined;
            // window.location.href = opts?.logoutParams?.returnTo || '/';
        });
      },

      async getUser(): Promise<User | undefined> {
        // Add similar ensureClientReady() wrapper
        const client = await ensureClientReady();
        return client.getUser();
      }
    };

    // Assign methods to Vue prototype under $auth namespace
    // Ensure reactive properties are spread first if mixing
     VueInstance.prototype.$auth = {
      ...authService, // Reactive properties
      ...methods      // Methods
    };

    // --- Initialization Function ---
    // This function now focuses ONLY on creating the client and initial session check
    async function initializeAuth0Internal(): Promise<Auth0Client> {
        console.log('[Auth Plugin] initializeAuth0Internal execution started.');
        try {
            const client = await createAuth0Client({
                ...options,
                useRefreshTokens: true, // Ensure refresh tokens are enabled in SDK options
                cacheLocation: options.cacheLocation || 'localstorage',
            });
            authService.auth0Client = client; // Assign the client instance to reactive state
            console.log('[Auth Plugin] Auth0 client CREATED and assigned.');

            // Initial session check ONLY if not on the callback route
            if (!window.location.search.includes('code=') || !window.location.search.includes('state=')) {
                console.log('[Auth Plugin] Not callback route, attempting initial session check...');
                await client.checkSession();
                authService.user = await client.getUser() || undefined;
                authService.isAuthenticated = await client.isAuthenticated();
                console.log('[Auth Plugin] Initial session check completed. isAuthenticated:', authService.isAuthenticated);
            } else {
                console.log('[Auth Plugin] Callback route detected during init. Skipping initial session check.');
                // Callback handling will occur via handleRedirectCallback method call
            }
            return client; // Return the created client
        } catch (e) {
            console.error('[Auth Plugin] CRITICAL: Auth0 client creation or initial checkSession failed:', e);
            authService.isAuthenticated = false;
            authService.user = undefined;
            // Rethrow? Or handle? Rethrowing might stop app load. Logging might be safer.
            // Depending on desired behavior, you might want to throw e;
            return Promise.reject(e); // Ensure promise returned by this function rejects on error
        } finally {
             // Initial overall loading state is now complete (or failed)
            // Note: handleRedirectCallback manages its own loading phase after this.
            authService.loading = false;
             console.log('[Auth Plugin] Initial client setup/check phase finished. Overall loading set to false.');
        }
    }

    // --- Start Initialization ---
    // Store the promise returned by the internal init function
    initializationPromise = initializeAuth0Internal();

  } // End install method
}; // End Auth0Plugin object

// Helper to use $auth with type safety in components
export function useAuth(vueInstance: Vue): Auth0Plugin['$auth'] {
  // Ensure $auth exists before returning
  if (!vueInstance.$auth) {
      // This might happen if useAuth is called before the plugin is fully installed/initialized
      // Depending on strictness, you could throw an error or return a dummy object
      console.error("useAuth called before $auth was initialized on Vue instance.");
      // Return a non-functional default or throw error:
      throw new Error("$auth is not available.");
  }
  return vueInstance.$auth;
}